// Error boundaries — React 18 unmounts the whole tree on an uncaught render
// error, so one throw in any page or panel used to leave #root empty. App
// wraps the routed page (keyed to the route, so leaving a broken page clears
// it), each overlay panel, and the root as a last resort.
//
//   <ErrorBoundary name="page" resetKey={routeKey}>…</ErrorBoundary>
//   <ErrorBoundary name="ask-atlas" fallback={null} onError={close}>…</ErrorBoundary>
//
// fallback: omitted → ErrorFallback (message + Reload); null → nothing;
// a function → fallback({ error, reset }).

// Self-contained on purpose (no Btn / primitives): the root boundary shows it
// when anything else may be what broke.
const ErrorFallback = ({ error }) => (
  <div role="alert" style={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 24, textAlign: 'center', background: 'var(--bg)' }}>
    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Something went wrong</div>
    {error && error.message && (
      <div style={{ maxWidth: 520, fontSize: 11.5, color: 'var(--text-3)', fontFamily: 'var(--font-mono)', overflowWrap: 'anywhere' }}>{error.message}</div>
    )}
    <button
      onClick={() => window.location.reload()}
      style={{ height: 30, padding: '0 14px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' }}
    >
      Reload
    </button>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, error: null, resetKey: props.resetKey };
    this.reset = () => this.setState({ failed: false, error: null });
  }

  // `failed`, not `error`: a bare `throw undefined` still shows the fallback.
  static getDerivedStateFromError(error) {
    return { failed: true, error };
  }

  // A new resetKey (the route) clears a caught error, so the next page
  // renders fresh instead of inheriting the last one's fallback.
  static getDerivedStateFromProps(props, state) {
    if (Object.is(props.resetKey, state.resetKey)) return null;
    return { failed: false, error: null, resetKey: props.resetKey };
  }

  // Production React doesn't rethrow a caught error to window.onerror, so
  // Sentry only hears about it from here.
  componentDidCatch(error, info) {
    try {
      window.Sentry?.captureException?.(error, {
        tags: { boundary: this.props.name || 'unnamed' },
        contexts: { react: { componentStack: (info && info.componentStack) || '' } },
      });
    } catch (_) { /* reporting must never throw from a boundary */ }
    if (this.props.onError) this.props.onError(error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    const { fallback } = this.props;
    if (fallback === undefined) return <ErrorFallback error={this.state.error} />;
    if (typeof fallback === 'function') return fallback({ error: this.state.error, reset: this.reset });
    return fallback;
  }
}

Object.assign(window, { ErrorBoundary, ErrorFallback });
